import InputControl from '../../../components/InputControl/InputControl';
import InteractiveButton from '../../../components/InteractiveButton/InteractiveButton';
import { FormikConfig, useFormik } from 'formik';
import { IPreHeatRecipeEditable, IRecipeEditable } from '../../../types/recipes-types';
import { fields, preHeatRecipeFields } from '../constants';
import React, { useEffect, useMemo, useState } from 'react';
import CycleSettings from '../../../enums/cycle-setting.enum';
import Switcher, { ResponseSwitch } from '../../../components/Switcher/Switcher';
import CycleMeasurement from '../../../enums/cycle-measurement';
import CycleMeasurementSymbol from '../../../enums/cycle-measurement-symbol';
import { convertAbsoluteTemperature, convertTemperatureDelta } from '../../../utils/temperature-unit-conversion';
import {
  createPostWeldRecipeValidationSchema,
  createPreHeatRecipeValidationSchema,
} from '../../../utils/recipe-unit-validation';

export interface RecipeFormProps {
  initialValues: IRecipeEditable | IPreHeatRecipeEditable;
  validationSchema: FormikConfig<IRecipeEditable | IPreHeatRecipeEditable>['validationSchema'];
  onSubmit: (values: IRecipeEditable | IPreHeatRecipeEditable) => any;
  formName: string;
  submitButtonName: string;
  enableTemperatureUnitSwitcher?: boolean;
}

const postWeldDeltaFields: (keyof IRecipeEditable)[] = [
  'rateUpperTolerance',
  'rateLowerTolerance',
  'holdUpperTolerance',
  'holdLowerTolerance',
];

const preHeatDeltaFields: (keyof IPreHeatRecipeEditable)[] = ['rate', 'soakUpper', 'soakLower'];

const preHeatAbsoluteFields: (keyof IPreHeatRecipeEditable)[] = ['finalTemp'];

const convertRecipeValues = (
  values: IRecipeEditable | IPreHeatRecipeEditable,
  from: CycleMeasurement,
  to: CycleMeasurement,
): IRecipeEditable | IPreHeatRecipeEditable => {
  if (values.mode === CycleSettings.PreHeat) {
    const convertedValues = { ...values };

    preHeatDeltaFields.forEach((key) => {
      (convertedValues as any)[key] = convertTemperatureDelta(convertedValues[key], from, to) as number;
    });

    preHeatAbsoluteFields.forEach((key) => {
      (convertedValues as any)[key] = convertAbsoluteTemperature(convertedValues[key], from, to) as number;
    });

    return convertedValues;
  }

  const convertedValues = { ...values };

  postWeldDeltaFields.forEach((key) => {
    (convertedValues as any)[key] = convertTemperatureDelta(convertedValues[key], from, to) as number;
  });

  return convertedValues;
};

export const RecipeForm: React.FC<RecipeFormProps> = ({
  initialValues,
  validationSchema,
  onSubmit,
  formName,
  submitButtonName,
  enableTemperatureUnitSwitcher = false,
}) => {
  const [measurement, setMeasurement] = useState(CycleMeasurement.Celsius);
  const activeValidationSchema = useMemo(
    () =>
      enableTemperatureUnitSwitcher
        ? initialValues.mode === CycleSettings.PostWeld
          ? createPostWeldRecipeValidationSchema(measurement)
          : createPreHeatRecipeValidationSchema(measurement)
        : validationSchema,
    [enableTemperatureUnitSwitcher, initialValues.mode, measurement, validationSchema],
  );

  const formik = useFormik<IRecipeEditable | IPreHeatRecipeEditable>({
    initialValues: initialValues,
    validationSchema: activeValidationSchema,
    onSubmit: (values) => {
      const celsiusValues = convertRecipeValues(values, measurement, CycleMeasurement.Celsius);

      return onSubmit(celsiusValues);
    },
  });

  const fieldList = initialValues.mode === CycleSettings.PostWeld ? fields : preHeatRecipeFields;

  useEffect(() => {
    if (enableTemperatureUnitSwitcher) {
      formik.validateForm();
    }
  }, [enableTemperatureUnitSwitcher, measurement]);

  const onUnitsChange = (data: ResponseSwitch) => {
    const nextMeasurement = data.value ? CycleMeasurement.Celsius : CycleMeasurement.Fahrenheit;
    const convertedValues = convertRecipeValues(formik.values, measurement, nextMeasurement);

    setMeasurement(nextMeasurement);
    formik.setValues(convertedValues, false);
  };

  const getFieldLabel = (item: (typeof fieldList)[number]) => {
    if (!enableTemperatureUnitSwitcher || item.key === 'name') {
      return item.label;
    }

    const symbol = CycleMeasurementSymbol[measurement];
    const isRateField = item.key === 'rate' || item.key === 'rateUpperTolerance' || item.key === 'rateLowerTolerance';
    const suffix = isRateField ? `${symbol}/h` : symbol;

    return `${item.label} (${suffix})`;
  };

  return (
    <div className="p-edit-controller pb-4 md:pb-25">
      <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="flex items-center justify-between gap-4 border-b border-stroke px-7 py-4 dark:border-strokedark">
          <h3 className="font-medium text-black dark:text-white">{formName}</h3>
          {enableTemperatureUnitSwitcher && (
            <div className="c-input-control__input-wrapper">
              <Switcher
                options={{
                  left: CycleMeasurementSymbol.Celsius,
                  right: CycleMeasurementSymbol.Fahrenheit,
                }}
                isEnable={measurement === CycleMeasurement.Celsius}
                onChange={onUnitsChange}
                switcherId={`${initialValues.mode}-recipe-units-id`}
              />
            </div>
          )}
        </div>
        <form className="flex flex-col gap-5.5 p-7" onSubmit={formik.handleSubmit}>
          <div className="w-full">
            {fieldList.map((item) => {
              return (
                <InputControl
                  key={item.key}
                  label={getFieldLabel(item)}
                  type={item.inputType}
                  name={item.key}
                  placeholder={item.placeholder}
                  value={formik.values[item.key] ?? ''}
                  customOnChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  showError={formik.touched[item.key] && formik.errors[item.key]}
                  errorText={formik.errors[item.key]}
                />
              );
            })}
            <InteractiveButton text={submitButtonName} submit isLoading={formik.isSubmitting} />
          </div>
        </form>
      </div>
    </div>
  );
};
